import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useModal } from 'react-native-modalfy';

import { PaymentMethod } from '@models/Viewer/PaymentMethodModel';
import {
  BaseText,
  Icon,
  ListView,
  Separator,
  SettingsLayout,
  Touchable,
} from '@components';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { screens, ScreensNameType } from '@navigation';
import { PaymentMethodItem } from './components/PaymentMethodItem';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { modals } from '@modals';

const SubTitle = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  variant: 'text.xlarge',
});

const Description = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
});

interface ICreditCardItemContainerStyleProps {
  index: number;
}

const CreditCardItemContainer = styled(View)(
  (props: ICreditCardItemContainerStyleProps) => ({
    flex: 0.5,
    minHeight: 170,
    pl: props.index % 2 === 1 ? '$8' : 0,
  }),
);

const AddNewCardContainer = styled(Touchable)({
  flex: 1,
  bg: '$background10',
  borderRadius: 6,
  alignItems: 'center',
  justifyContent: 'center',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
});

const AddNewCardText = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
  paddingLeft: '$5',
});

const BottomSpace = styled(View)({
  size: 28,
});

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Subscription,
  screens.CreditCards,
];

// type NavigationPropType =
//   ProfileNavigatorNavigationPropType<'CreditCards'>;

export const CreditCardsScreen = observer(() => {
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const modal = useModal();
  // const nav = useNavigation<NavigationPropType>();

  useNavOptionsSet({ title: t('creditCards.title') });

  useAsyncInit(async () => {
    try {
      await viewer.paymentMethodList.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  function openAddCardModal() {
    modal.openModal(modals.AddCreditCardModal);
  }

  // function navigateToCreditCard(item: PaymentMethod) {
  //   nav.navigate(screens.UpdateCreditCard, {
  //     id: item.id,
  //   });
  // }

  async function deleteCard(item: PaymentMethod) {
    try {
      await item.delete.run();

      ToastService.showSuccess(t('creditCards.successDeleteMessage'));
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function openDeleteCardModal(item: PaymentMethod) {
    if (item.isDefault) {
      ToastService.showError(t('creditCards.cannotRemoveDefault'));
    } else {
      modal.openModal(modals.ConfirmationModal, {
        title: t('creditCards.deleteCard'),
        subtitle: t('creditCards.deleteCardSubtitle'),
        buttons: [
          { text: t('creditCards.delete') },
          { text: t('creditCards.cancel') },
        ],
        onConfirm: () => {
          deleteCard(item);
          modal.closeModal();
        },
      });
    }
  }

  const data = useMemo(
    () => [
      ...viewer.paymentMethodList.asArray,
      modals.AddCreditCardModal,
    ],
    [viewer.paymentMethodList.asArray],
  );

  return (
    <SettingsLayout
      title={t('creditCards.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <Separator size="$6" />
      <SubTitle>{t('creditCards.yourCards')}</SubTitle>
      <Separator size="$5" />
      <Description>
        {t('creditCards.selectDefaultPaymentMethod')}
      </Description>
      <Separator size="$8" />

      <ListView
        isLoading={viewer.paymentMethodList.fetch.inProgress}
        numColumns={2}
        data={data}
        keyExtractor={(i) =>
          typeof i === 'string' ? i : i.id.toString()
        }
        renderItem={({ item, index }) => {
          if (item === modals.AddCreditCardModal) {
            return (
              <CreditCardItemContainer index={index}>
                <AddNewCardContainer onPress={openAddCardModal}>
                  <RowContainer>
                    <Icon
                      name="plus-light"
                      color="$heading800"
                      size={22}
                    />
                    <AddNewCardText>
                      {t('creditCards.addNewCard')}
                    </AddNewCardText>
                  </RowContainer>
                </AddNewCardContainer>
                <BottomSpace />
              </CreditCardItemContainer>
            );
          }

          return (
            <CreditCardItemContainer index={index}>
              <PaymentMethodItem
                item={item}
                onPressDelete={openDeleteCardModal}
                // onPressEdit={navigateToCreditCard}
              />
            </CreditCardItemContainer>
          );
        }}
        ItemSeparatorComponent={() => <Separator size="$8" />}
      />
    </SettingsLayout>
  );
});
